import React, { useState } from 'react';
import { PhoneIcon, MessageCircleIcon, UserIcon, SparklesIcon, LockIcon } from './Icons';
import { useClient } from '../contexts/ClientContext';
import { Logo } from './Logo';

interface HeaderProps {
    currentPage: string;
    onNavigate: (page: string) => void;
    onOpenProfile?: () => void;
    onOpenSarah?: () => void;
    onRecharge?: () => void;
    unreadMessagesCount?: number;
}

export const Header: React.FC<HeaderProps> = ({ 
    currentPage, 
    onNavigate, 
    onOpenProfile, 
    onOpenSarah, 
    onRecharge,
    unreadMessagesCount = 0
}) => {
  const { client, openAuthModal, logoutClient } = useClient();

  const handleNav = (page: string) => {
      onNavigate(page);
  };

  const handleProfileClick = () => {
      if (onOpenProfile) onOpenProfile();
      else onNavigate('profile');
  };

  const handleRechargeClick = () => {
      if (onRecharge) onRecharge();
      else {
          onNavigate('tarifs');
          if (onOpenSarah) onOpenSarah();
      }
  };

  return (
    <header className={`sticky top-0 z-40 w-full bg-[#08081A]/90 backdrop-blur-xl border-b border-purple-900/40 shadow-[0_4px_30px_rgba(0,0,0,0.7)] transition-all ${currentPage === "messages" ? "hidden lg:block" : "block"}`}>

      <div className="container mx-auto px-3 sm:px-6 py-3 flex justify-between items-center">
        
        {/* Official Brand Logo */}
        <div className="flex items-center gap-3">
            <Logo 
                size="md" 
                showTagline={false}
                onClick={() => handleNav('home')} 
            />
        </div>

        {/* Desktop Navigation Links */}
        <nav className="hidden xl:flex gap-1.5 text-xs font-semibold items-center bg-[#12102A]/80 border border-purple-900/40 p-1 rounded-full shadow-inner">
          
          {[
            { id: 'home', label: 'Accueil' },
            { id: 'mediums', label: 'Nos Médiums', badge: 'LIVE' },
            { id: 'tarot', label: 'Tarot' },
            { id: 'love', label: 'Love Match' },
            { id: 'dreams', label: 'Rêves' },
            { id: 'numerology', label: 'Numérologie' },
            { id: 'forum', label: 'Forums' },
            { id: 'tarifs', label: 'Tarifs' },
          ].map(item => {
            const isActive = currentPage === item.id || (item.id === 'home' && (!currentPage || currentPage === 'accueil'));
            return (
              <button
                key={item.id}
                onClick={() => handleNav(item.id)}
                className={`px-3.5 py-1.5 rounded-full transition-all duration-200 cursor-pointer flex items-center gap-1.5 ${
                  isActive
                    ? 'bg-gradient-to-r from-purple-700 to-indigo-700 text-white font-bold shadow-[0_0_15px_rgba(108,43,217,0.5)] scale-105'
                    : 'text-purple-200/80 hover:text-white hover:bg-white/5'
                }`}
              >
                <span>{item.label}</span>
                {item.badge && (
                  <span className="text-[9px] bg-red-600 text-white font-black px-1.5 py-0.2 rounded-full animate-pulse">
                    {item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Right Action Controls (Desktop + Mobile) */}
        <div className="flex items-center gap-2.5 sm:gap-3">
          
          {/* Numéro Audiotel Direct */}
          <a
            href="tel:0985405274"
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-950/80 text-emerald-300 border border-emerald-500/40 hover:border-emerald-400 text-xs font-bold transition-all shadow-sm group"
            title="Appeler la voyance Audiotel sans attente (0,80€/min)"
          >
            <PhoneIcon className="w-3.5 h-3.5 text-emerald-400 group-hover:animate-bounce" />
            <span className="font-mono">09 85 40 52 74</span>
          </a>

          {/* Shortcut Messages (Desktop - Visible UNIQUEMENT si client connecté) */}
          {client && (
            <button
              onClick={() => onNavigate('messages')}
              className="relative hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-950/60 hover:bg-purple-900/80 text-purple-200 hover:text-white border border-purple-800/40 text-xs font-bold transition-all cursor-pointer"
              title="Boîte de réception & messages privés"
            >
              <MessageCircleIcon className="w-3.5 h-3.5 text-purple-400" />
              <span className="hidden md:inline">Messages</span>
              {unreadMessagesCount > 0 && (
                <span className="bg-red-600 text-white text-[9px] font-black w-4 h-4 rounded-full flex items-center justify-center animate-pulse">
                  {unreadMessagesCount}
                </span>
              )}
            </button>
          )}

          {/* Espace Client / Authentification */}
          {client ? (
            <div className="flex items-center gap-2">
              {/* Bouton Recharger mon compte */}
              <button
                onClick={handleRechargeClick}
                className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-gold-500 via-yellow-400 to-gold-500 hover:from-gold-400 hover:to-yellow-300 text-black font-black text-xs rounded-full shadow-[0_0_15px_rgba(251,191,36,0.35)] transition-all cursor-pointer active:scale-95"
              >
                <span>{client.loyaltyPoints || 0} Astres</span>
              </button>

              {/* Bouton Profil */}
              <button
                onClick={handleProfileClick}
                className="flex items-center gap-2 bg-[#12102A] hover:bg-[#1A1033] border border-purple-500/40 hover:border-gold-400 text-white px-3 py-1.5 rounded-full text-xs font-bold transition-all shadow-md active:scale-95 cursor-pointer"
              >
                {client.avatar ? (
                  <img src={client.avatar} alt="Avatar" className="w-5 h-5 rounded-full object-cover" />
                ) : (
                  <div className="w-5 h-5 bg-gradient-to-br from-gold-400 to-amber-600 rounded-full flex items-center justify-center text-black font-black text-[10px]">
                    {client.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="hidden sm:inline max-w-[90px] truncate">{client.name}</span>
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={openAuthModal}
                className="flex items-center gap-1.5 bg-gradient-to-r from-purple-700 via-indigo-600 to-purple-700 hover:from-purple-600 hover:to-indigo-500 text-white px-4 py-2 rounded-full text-xs font-black shadow-[0_0_20px_rgba(108,43,217,0.4)] transition-all border border-purple-400/40 active:scale-95 cursor-pointer"
              >
                <LockIcon className="w-3.5 h-3.5 text-amber-300" />
                <span>Commencer</span>
              </button>
            </div>
          )}

        </div>
      </div>
    </header>
  );
};

export default Header;
